import { useState, useEffect, useContext, useMemo } from "react";
import UserContext from "../../../context/UserContext";
import MembershipContext from "../../../context/MembershipContext";
import ProductApi from "../../../api/ProductApi";
import Payment from "../../../util/Payment";
import { loginAlert } from "../../common/CustomAlert";

const MEMBERSHIP_PROD_TYPE = "PRODT03";

const BADGE_IMAGES = {
    bestSelling: "/static/img/assets/membership_label_02.png",
    bestOptions: "/static/img/assets/membership_label_03.png",
};

// 할인율 적용 후 "100원 단위로 버림" (예: 9130원 -> 9100원)
const calcDiscountedPrice = (price, disc = 0) => {
    const raw = Number(price) * (100 - Number(disc || 0)) * 0.01;
    return Math.floor(raw / 100) * 100;
};

const parseBenefits = (desc) => {
    if (!desc) return [];
    return desc
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
};

const getCardMeta = (product) => {
    const adv = product?.prod_adv;

    if (adv === "bestOptions") {
        return {
            className: "membership-card membership-card--premium",
            badge: BADGE_IMAGES.bestOptions,
            showRecommend: true,
        };
    }

    if (adv === "bestSelling") {
        return {
            className: "membership-card membership-card--standard",
            badge: BADGE_IMAGES.bestSelling,
            showRecommend: false,
        };
    }

    return {
        className: "membership-card",
        badge: null,
        showRecommend: false,
    };
};

const MembershipWriteCard = ({ product, selectedProdId, onSelect }) => {
    const meta = getCardMeta(product);
    const discountedPrice = calcDiscountedPrice(product.prod_price, product.prod_disc);
    const benefits = parseBenefits(product.prod_desc);
    const showOriginPrice = Number(product.prod_disc) > 0;

    return (
        <article className={meta.className}>
            {meta.badge && (
                <span className="membership-card-badge">
                    <img src={meta.badge} alt="" />
                </span>
            )}
            {meta.showRecommend ? (
                <div className="membership-card-head">
                    <h3>{product.prod_title}</h3>
                    <span className="membership-card-recommend">추천</span>
                </div>
            ) : (
                <h3>{product.prod_title}</h3>
            )}
            {showOriginPrice && (
                <div className="membership-card-origin-price">
                    {Number(product.prod_price).toLocaleString()}원
                </div>
            )}
            <div className="membership-card-price">
                <strong>{discountedPrice.toLocaleString()}</strong>
                <span>원/월</span>
            </div>
            <label className="membership-card-select">
                <input
                    type="radio"
                    name="membership-grade"
                    value={product.prod_id}
                    checked={selectedProdId === product.prod_id}
                    onChange={() => onSelect(product.prod_id)}
                />
                <span className="membership-card-select-button">선택</span>
            </label>
            <ul className="membership-card-benefit">
                {benefits.map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                ))}
            </ul>
        </article>
    );
};

const MembershipWrite = () => {
    const { user, updateUserInfo } = useContext(UserContext);
    const { refreshWriteMembership } = useContext(MembershipContext);
    const [products, setProducts] = useState([]);
    const [selectedProdId, setSelectedProdId] = useState(null);
    const [loading, setLoading] = useState(true);

    const sortedProducts = useMemo(
        () => [...products].sort((a, b) => Number(a.prod_price) - Number(b.prod_price)),
        [products]
    );

    const selectedProduct = useMemo(
        () => sortedProducts.find((p) => p.prod_id === selectedProdId) || null,
        [sortedProducts, selectedProdId]
    );

    useEffect(() => {
        ProductApi.getMembershipOfWriteProduct()
            .then((list) => {
                const membershipProducts = (list || []).filter(
                    (p) => p.prod_type === MEMBERSHIP_PROD_TYPE
                );
                const sorted = [...membershipProducts].sort(
                    (a, b) => Number(a.prod_price) - Number(b.prod_price)
                );
                setProducts(sorted);
                if (sorted.length > 0) {
                    const defaultProduct =
                        sorted.find((p) => p.prod_adv === "bestSelling") ||
                        sorted[0];
                    setSelectedProdId(defaultProduct.prod_id);
                }
            })
            .catch((err) => {
                console.error("멤버쉽 상품 조회 실패:", err);
            })
            .finally(() => setLoading(false));
    }, []);

    const handlePayBtn = () => {
        if (!user?.userId) return loginAlert("로그인이 필요합니다.");
        if (!selectedProduct) return alert("멤버쉽 상품을 선택해 주세요.");

        const payUser = {
            userEmail: user.userEmail,
            userNm: user.userNm,
            userPhone: user.userPhone,
        };

        const data = {
            prodId: String(selectedProduct.prod_id),
            prodTitle: `멤버쉽 ${selectedProduct.prod_title}`,
            prodDisc: 0,
            prodPrice: calcDiscountedPrice(selectedProduct.prod_price, selectedProduct.prod_disc),
        };

        Payment.openPaymentPage(data, payUser, "/order/membership/complete", () => {
            updateUserInfo();
            refreshWriteMembership();
        });
    };

    return (
        <section className="membership-section">
            <div className="membership-banner">
                <div className="membership-banner-content">
                    <div className="membership-banner-text">
                        <h2 className="membership-banner-title">지금 멤버십 할인 혜택받고 <br /> 신규 고객을 만나보세요</h2>
                        <p className="membership-banner-description">
                            할인된 멤버십으로 마케팅서비스를 등록하고
                            더 많은 고객과 연결해보세요.
                            지금만 가능한 혜택, 놓치지 마세요.
                        </p>
                    </div>
                    <div className="membership-banner-visual" aria-hidden="true">
                        <img src="/static/img/assets/banner_membership.png" alt="" />
                    </div>
                </div>
            </div>

            <div className="membership-list">
                {loading && <p>상품을 불러오는 중입니다.</p>}
                {!loading && sortedProducts.length === 0 && (
                    <p>등록된 멤버쉽 상품이 없습니다.</p>
                )}
                {sortedProducts.map((product) => (
                    <MembershipWriteCard
                        key={product.prod_id}
                        product={product}
                        selectedProdId={selectedProdId}
                        onSelect={setSelectedProdId}
                    />
                ))}
            </div>

            <div className="btn_area">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePayBtn}
                    disabled={!selectedProduct}
                >
                    <span>결제하기</span>
                </button>
            </div>
        </section>
    );
};

export default MembershipWrite;
